import {
  AdvancedSearchFilter,
  Alert,
  Col,
  EditableInput,
  Loading,
  Panel,
  RadioButton,
  Row,
  SearchPagination,
  SectionTitle,
  Table,
  usePagedQuery
} from '@elotech/components';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { PesquisaCadastroService } from '../../../service';
import {
  camposImobiliario,
  camposLocalizacaoFiltrosImobiliario,
  camposLocalizacaoFiltrosRural,
  camposRural
} from '../../../src/enums/CamposPesquisaCadastro';
import {
  aplicarConfiguracaoFiltros,
  getCamposLocalizacaoRural
} from '../../../utils/InscricaoCadastralUtils';

const tiposImovel = [
  { key: 'URBANO', value: 'URBANO', label: 'Urbano' },
  { key: 'RURAL', value: 'RURAL', label: 'Rural' }
];

/**
 * As colunas de localização seguem a parametrização de campos ativos de
 * Tributos > Configurações > Inscrição Cadastral, na ordem da inscrição.
 */
const getColunasUrbano = camposLocalizacao => [
  { header: 'Cadastro', value: row => row.cadastroGeral },
  { header: 'Inscrição Cadastral', value: row => row.inscricaoCadastral },
  { header: 'Proprietário', value: row => row.pessoaNome },
  ...camposLocalizacao.map(({ campo, label }) => ({
    header: label,
    value: row => row[campo]
  })),
  { header: 'Complemento do Lote', value: row => row.complementoLote },
  { header: 'Área Terreno', value: row => row.areaLote },
  { header: 'Área Construída', value: row => row.areaTotalConstruida },
  { header: 'CEP', value: row => row.cep },
  { header: 'Endereço', value: row => row.enderecoCompleto },
  { header: 'Complemento', value: row => row.complemento },
  { header: 'Bairro', value: row => row.bairro },
  { header: 'Cidade', value: row => row.cidade },
  { header: 'Estado', value: row => row.estado },
  { header: 'Condomínio', value: row => row.nomeCondominio },
  { header: 'Situação', value: row => row.situacao }
];

// A pesquisa rural devolve distrito e setor pela descrição (ex.: "DISTRITO 04").
const COLUNA_PESQUISA_RURAL = {
  distrito: 'distritoDescricao',
  setor: 'setorDescricao',
  lote: 'lote'
};

const getColunasRural = camposLocalizacao => [
  { header: 'Cadastro', value: row => row.cadastroGeral },
  { header: 'Proprietário', value: row => row.pessoaNome },
  { header: 'Propriedade', value: row => row.nomePropriedade },
  { header: 'Endereço', value: row => row.endereco },
  ...getCamposLocalizacaoRural(camposLocalizacao).map(({ campo, label }) => ({
    header: label,
    value: row => row[COLUNA_PESQUISA_RURAL[campo]]
  })),
  { header: 'Matrícula', value: row => row.matricula }
];

const mapCadastroSelecionadoToRow = (
  cadastroSelecionado,
  isUrbano,
  camposLocalizacao = []
) =>
  isUrbano
    ? {
        cadastroGeral: cadastroSelecionado.cadastro,
        inscricaoCadastral: cadastroSelecionado.inscricaoCadastral,
        pessoaNome: cadastroSelecionado.proprietario,
        enderecoCompleto: cadastroSelecionado.endereco,
        // O cadastro replicado no ITBI nomeia os campos de localização de forma
        // diferente do retorno da pesquisa (ex.: setor é "zona"), por isso o de-para.
        ...camposLocalizacao.reduce(
          (acc, { campo, campoCadastro }) => ({
            ...acc,
            [campo]: cadastroSelecionado[campoCadastro]
          }),
          {}
        ),
        complementoLote: cadastroSelecionado.complementoLote,
        areaLote: cadastroSelecionado.areaTerreno,
        areaTotalConstruida: cadastroSelecionado.areaConstruida,
        cep: cadastroSelecionado.cep,
        complemento: cadastroSelecionado.complemento,
        bairro: cadastroSelecionado.bairro,
        cidade: cadastroSelecionado.cidade,
        estado: cadastroSelecionado.estado,
        nomeCondominio: cadastroSelecionado.descricaoCondominio
      }
    : {
        cadastroGeral: cadastroSelecionado.cadastro,
        pessoaNome: cadastroSelecionado.nome,
        endereco: cadastroSelecionado.enderecoDescrito,
        distritoDescricao: cadastroSelecionado.distrito,
        setorDescricao: cadastroSelecionado.setor,
        lote: cadastroSelecionado.lote,
        matricula: cadastroSelecionado.numeroIncra
      };

const PesquisaCadastroImovel = ({
  tipoImovel,
  onChangeTipoImovel,
  permiteItbiRural,
  onSelect,
  onChangeNumero,
  cadastroSelecionado,
  loadingSelecao,
  camposLocalizacao = []
}) => {
  const isUrbano = tipoImovel === 'URBANO';
  const [searchFilters, setSearchFilters] = useState([]);

  const {
    loading,
    values,
    pagination,
    doSearch,
    doPagedSearch,
    resetValues
  } = usePagedQuery({
    search: (filtros, pageRequest) =>
      PesquisaCadastroService.advancedSearch(
        isUrbano,
        filtros,
        pageRequest?.page,
        pageRequest?.size
      ).then(response => ({ ...response, data: response.data.cadastros })),
    onError: error =>
      Alert.error({ title: 'Não foi possível pesquisar os cadastros.' }, error),
    disableQueryPagination: true
  });

  useEffect(() => {
    setSearchFilters([]);
    resetValues();
  }, [tipoImovel]);

  useEffect(() => {
    const cadastro = cadastroSelecionado?.cadastro;
    if (
      cadastro != null &&
      pagination !== undefined &&
      !(values || []).some(row => row.cadastroGeral === cadastro)
    ) {
      resetValues();
    }
  }, [cadastroSelecionado?.cadastro]);

  const campos = isUrbano
    ? aplicarConfiguracaoFiltros(
        camposImobiliario,
        camposLocalizacaoFiltrosImobiliario,
        camposLocalizacao
      )
    : aplicarConfiguracaoFiltros(
        camposRural,
        camposLocalizacaoFiltrosRural,
        getCamposLocalizacaoRural(camposLocalizacao)
      );

  const buscar = filtros => {
    const filtrosPreenchidos = (filtros || []).filter(filtro =>
      (filtro.value || '').trim()
    );

    if (!filtrosPreenchidos.length) {
      Alert.warning({ title: 'Informe ao menos um filtro para pesquisar.' });
      return;
    }

    doSearch(filtrosPreenchidos);
  };

  const colunas = isUrbano
    ? getColunasUrbano(camposLocalizacao)
    : getColunasRural(camposLocalizacao);
  const rows =
    pagination !== undefined
      ? values
      : cadastroSelecionado?.cadastro
      ? [
          mapCadastroSelecionadoToRow(
            cadastroSelecionado,
            isUrbano,
            camposLocalizacao
          )
        ]
      : [];
  const exibeTabela = pagination !== undefined || rows.length > 0;

  return (
    <div>
      <Loading loading={loading || loadingSelecao} />
      <Row>
        {permiteItbiRural && (
          <Col sm={2}>
            <div className="form-group">
              <label className="label" htmlFor="filtroTipoImovel">
                Tipo do Imóvel
              </label>
              <select
                value={tipoImovel}
                onChange={e => onChangeTipoImovel(e.target.value)}
                id="filtroTipoImovel"
                style={{ height: 28 }}
              >
                {tiposImovel.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </Col>
        )}
        <Col
          sm={permiteItbiRural ? 10 : 12}
          style={{ position: 'relative', zIndex: 4 }}
        >
          <label className="label"></label>
          <AdvancedSearchFilter
            onFilter={buscar}
            fields={campos}
            placeholder={
              searchFilters.length !== 0
                ? ''
                : 'Selecione um filtro, digite o valor e pressione TAB'
            }
            screenName={`declaracao-itbi-${tipoImovel}`}
            searchFilters={searchFilters}
            onChangeSearchFilters={setSearchFilters}
            filterInputSize={11}
          />
        </Col>
      </Row>
      {exibeTabela && (
        <>
          <SectionTitle>Selecione um cadastro para a declaração</SectionTitle>
          <Panel isTable>
            <Table values={rows} keyExtractor={row => row.cadastroGeral}>
              <Table.Column
                name="select-cadastro"
                header=""
                value={row => (
                  <RadioButton
                    selectedValue={
                      cadastroSelecionado?.cadastro === row.cadastroGeral
                    }
                    name="cadastroSelecionado"
                    id={`selecionarCadastro-${row.cadastroGeral}`}
                    onChange={() => onSelect(row.cadastroGeral)}
                  />
                )}
              />
              {colunas.map(coluna => (
                <Table.Column
                  key={coluna.header}
                  header={coluna.header}
                  value={coluna.value}
                />
              ))}
              {isUrbano && (
                <Table.Column
                  name="numero-endereco"
                  header="Número"
                  value={row =>
                    cadastroSelecionado?.cadastro === row.cadastroGeral ? (
                      <EditableInput
                        name="numero"
                        maxLength={10}
                        initialValue={cadastroSelecionado.numero}
                        onConfirmChange={value => onChangeNumero(value)}
                      />
                    ) : (
                      row.numero ?? null
                    )
                  }
                />
              )}
            </Table>
            {pagination && (
              <SearchPagination
                page={pagination}
                searchWithPage={doPagedSearch}
              />
            )}
          </Panel>
        </>
      )}
    </div>
  );
};

PesquisaCadastroImovel.propTypes = {
  tipoImovel: PropTypes.oneOf(['URBANO', 'RURAL']).isRequired,
  onChangeTipoImovel: PropTypes.func,
  permiteItbiRural: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onChangeNumero: PropTypes.func,
  cadastroSelecionado: PropTypes.object,
  loadingSelecao: PropTypes.bool
};

export default PesquisaCadastroImovel;
